import { useState, useEffect, useCallback } from 'react';
import { Clock, CheckCircle2, Play, User, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { yardApi, YardPutawayTask, YardStorageLocation } from '@/api/services/yard.api';
import {
  PageHeader,
  MetricCard,
  KPIGrid,
  StatusBadge,
  DataTable,
  Column,
  TableFilters,
} from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function YardPutaway() {
  const [tasks, setTasks] = useState<YardPutawayTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completedToday: 0,
    avgCompletionTime: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');

  // Selected task for details
  const [selectedTask, setSelectedTask] = useState<YardPutawayTask | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Complete dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<YardPutawayTask | null>(null);
  const [completeForm, setCompleteForm] = useState({
    storageLocationId: '',
    notes: '',
  });
  const [storageLocations, setStorageLocations] = useState<YardStorageLocation[]>([]);

  // Suggested location
  const [suggestedLocation, setSuggestedLocation] = useState<YardStorageLocation | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const response = await yardApi.putaway.getAll(params);
      setTasks(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      setTasks([]);
      console.error('Error fetching putaway tasks:', error);
      toast.error('Failed to load putaway tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);
  const fetchStats = useCallback(async () => {
    try {
      const response = await yardApi.putaway.getStats();
      const defaults = { pending: 0, inProgress: 0, completedToday: 0, avgCompletionTime: 0 };
      const statsData = typeof response === 'object' && response !== null ? response : {};
      setStats({ ...defaults, ...statsData });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [fetchTasks, fetchStats]);

  const fetchStorageLocations = async () => {
    try {
      const response = await yardApi.storageLocations.getAll({ status: 'AVAILABLE' });
      setStorageLocations(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      console.error('Error fetching storage locations:', error);
    }
  };

  const handleStartTask = async (task: YardPutawayTask) => {
    try {
      await yardApi.putaway.start(task.id);
      toast.success('Putaway task started');
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error('Failed to start task');
    }
  };

  const handleOpenCompleteDialog = async (task: YardPutawayTask) => {
    setTaskToComplete(task);
    setCompleteForm({
      storageLocationId: task.storageLocationId || '',
      notes: '',
    });

    // Fetch storage locations and suggested location
    await fetchStorageLocations();

    if (task.item?.id) {
      try {
        const response = await yardApi.storageLocations.suggestLocation(task.item.id);
        setSuggestedLocation(response.suggested);
        if (response.suggested?.id) {
          setCompleteForm((prev) => ({ ...prev, storageLocationId: response.suggested.id }));
        }
      } catch (error) {
        console.error('Error getting suggested location:', error);
      }
    }

    setCompleteDialogOpen(true);
  };

  const handleCompleteTask = async () => {
    if (!taskToComplete) return;

    if (!completeForm.storageLocationId) {
      toast.error('Please select a storage location');
      return;
    }

    try {
      await yardApi.putaway.complete(taskToComplete.id, {
        storageLocationId: completeForm.storageLocationId,
        putawayQty: taskToComplete.quantity,
        notes: completeForm.notes || undefined,
      });
      toast.success('Putaway task completed');
      setCompleteDialogOpen(false);
      setTaskToComplete(null);
      setSuggestedLocation(null);
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleViewDetails = (task: YardPutawayTask) => {
    setSelectedTask(task);
    setIsSheetOpen(true);
  };

  const getPriorityBadge = (priority: number) => {
    if (priority <= 3) return <Badge variant="destructive">High</Badge>;
    if (priority <= 6) return <Badge variant="outline">Medium</Badge>;
    return <Badge variant="secondary">Low</Badge>;
  };

  const filteredTasks = tasks.filter((task) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTaskNumber = task.taskNumber?.toLowerCase().includes(query);
      const matchesItem = task.item?.name?.toLowerCase().includes(query);
      const matchesSku = task.item?.sku?.toLowerCase().includes(query);
      if (!matchesTaskNumber && !matchesItem && !matchesSku) return false;
    }
    return true;
  });

  const putawayColumns: Column<YardPutawayTask>[] = [
    {
      key: 'taskNumber',
      header: 'Task #',
      sortable: true,
      render: (task) => <span className="font-medium">{task.taskNumber}</span>,
    },
    {
      key: 'item',
      header: 'Item',
      render: (task) => (
        <div>
          <p className="font-medium">{task.item?.name || 'Unknown Item'}</p>
          <p className="text-xs leading-4 text-muted-foreground">{task.item?.sku}</p>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (task) => (
        <span>
          {task.quantity} / {task.putawayQty} put away
        </span>
      ),
    },
    {
      key: 'sourceType',
      header: 'Source',
      render: (task) => <Badge variant="outline">{task.sourceType}</Badge>,
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (task) => getPriorityBadge(task.priority),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (task) => <StatusBadge status={task.status} />,
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      render: (task) =>
        task.assignedTo ? (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span>{task.assignedTo.fullName}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">Unassigned</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (task) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(task);
            }}
          >
            View
          </Button>
          {task.status === 'PENDING' && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleStartTask(task);
              }}
            >
              <Play className="h-4 w-4 mr-1" />
              Start
            </Button>
          )}
          {task.status === 'IN_PROGRESS' && (
            <Button
              variant="default"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCompleteDialog(task);
              }}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Complete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Putaway Tasks"
          description="Directed tasks placing received stock into yard locations, with the bin assigned to each."
          actions={
            <Button
              variant="outline"
              onClick={() => {
                fetchTasks();
                fetchStats();
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Pending Tasks"
            value={stats.pending}
            icon={<Clock className="h-5 w-5" />}
            sentiment="warning"
          />
          <MetricCard
            label="In Progress"
            value={stats.inProgress}
            icon={<Play className="h-5 w-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Completed Today"
            value={stats.completedToday}
            icon={<CheckCircle2 className="h-5 w-5" />}
            sentiment="success"
          />
          <MetricCard
            label="Avg Completion Time"
            value={`${stats.avgCompletionTime} min`}
            icon={<Clock className="h-5 w-5" />}
            sentiment="neutral"
          />
        </KPIGrid>
      </motion.div>

      {/* Filters */}
      <motion.div variants={fadeUp}>
        {/* Tasks Table */}
        <DataTable<YardPutawayTask>
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search by task number, item name, or SKU..."
              filters={[
                {
                  id: 'status',
                  label: 'Status',
                  value: statusFilter,
                  options: [
                    { value: 'PENDING', label: 'Pending' },
                    { value: 'IN_PROGRESS', label: 'In Progress' },
                    { value: 'COMPLETED', label: 'Completed' },
                    { value: 'CANCELLED', label: 'Cancelled' },
                  ],
                  onChange: setStatusFilter,
                  placeholder: 'Status',
                },
                {
                  id: 'priority',
                  label: 'Priority',
                  value: priorityFilter,
                  options: [
                    { value: 'high', label: 'High (1-3)' },
                    { value: 'medium', label: 'Medium (4-6)' },
                    { value: 'low', label: 'Low (7-10)' },
                  ],
                  onChange: setPriorityFilter,
                  placeholder: 'Priority',
                },
              ]}
            />
          }
          onRowClick={(task) => handleViewDetails(task)}
          pagination
          data={filteredTasks}
          columns={putawayColumns}
          loading={loading}
          emptyTitle="No putaway tasks found"
          emptyDescription="No putaway tasks match your current filters."
        />

        {/* Task Details Sheet */}
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetContent className="sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>Task Details</SheetTitle>
              <SheetDescription>Putaway task information and status</SheetDescription>
            </SheetHeader>
            {selectedTask && (
              <div className="mt-6 space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Task Number</span>
                    <span className="font-medium">{selectedTask.taskNumber}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <StatusBadge status={selectedTask.status} />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Priority</span>
                    {getPriorityBadge(selectedTask.priority)}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Item Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Name</span>
                      <span>{selectedTask.item?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">SKU</span>
                      <span>{selectedTask.item?.sku}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Quantity</span>
                      <span>{selectedTask.quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Put Away Qty</span>
                      <span>{selectedTask.putawayQty}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Source Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Source Type</span>
                      <Badge variant="outline">{selectedTask.sourceType}</Badge>
                    </div>
                    {selectedTask.sourceId && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Source ID</span>
                        <span className="text-sm">{selectedTask.sourceId}</span>
                      </div>
                    )}
                  </div>
                </div>

                {selectedTask.storageLocation && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Storage Location</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Code</span>
                        <span>{selectedTask.storageLocation.code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Name</span>
                        <span>{selectedTask.storageLocation.name}</span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTask.notes && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground">{selectedTask.notes}</p>
                  </div>
                )}

                <div className="border-t pt-4 flex gap-2">
                  {selectedTask.status === 'PENDING' && (
                    <Button
                      className="flex-1"
                      onClick={() => {
                        handleStartTask(selectedTask);
                        setIsSheetOpen(false);
                      }}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start Task
                    </Button>
                  )}
                  {selectedTask.status === 'IN_PROGRESS' && (
                    <Button
                      className="flex-1"
                      onClick={() => {
                        handleOpenCompleteDialog(selectedTask);
                        setIsSheetOpen(false);
                      }}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete Task
                    </Button>
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Complete Task Dialog */}
        <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Putaway Task</DialogTitle>
              <DialogDescription>
                Select the storage location where the items will be placed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {suggestedLocation && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-700">Suggested Location</p>
                  <p className="text-sm text-blue-600">
                    {suggestedLocation.code} - {suggestedLocation.name}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="storageLocation">Storage Location *</Label>
                <Select
                  value={completeForm.storageLocationId}
                  onValueChange={(value) =>
                    setCompleteForm((prev) => ({ ...prev, storageLocationId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select storage location" />
                  </SelectTrigger>
                  <SelectContent>
                    {storageLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.code} - {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any notes about this putaway..."
                  value={completeForm.notes}
                  onChange={(e) => setCompleteForm((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCompleteTask}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Complete Putaway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </motion.main>
  );
}
